import { call, put, select, takeEvery } from "redux-saga/effects";

import { format, getIsUserSignedIn } from "@gamedev-web/gamedev-sdk-core";

import { DefaultNewGameConceptId, PageType, siteLocationPrefix } from "../../constants";
import {
  FormIdSearchParam,
  GameConceptIdSearchParam,
  PitchConceptIdSearchParam,
  ProgramSearchParam,
  SubFormIdSearchParam,
} from "../../helpers/dataFetchingHelpers";
import { OnboardingErrorType } from "../../models/errors";
import { SubFormsDataTypes } from "../../models/forms";
import { Programs } from "../../models/programsConfig";
import { AaaGameInformationFormId } from "../../pages/AaaGameInformationPage/AaaGameInformationPageConstants";
import { GameInformationFormId } from "../../pages/GameInformationPage/GameInformationPageConstants";
import { PitchInformationFormId } from "../../pages/PitchInformationPage/PitchInformationPageConstants";
import {
  AaaGameConceptResponse,
  IdXboxGameConceptResponse,
  PitchConceptResponse,
  ProgramsDataResponse,
} from "../../server/crm/api/models/forms";
import { capabilitiesActions } from "../actions/capabilitiesActions";
import { CrmActions, crmActions } from "../actions/crmApiActions";
import { gameConceptActions } from "../actions/gameConceptActions";
import { pitchConceptActions } from "../actions/pitchConceptActions";
import { programsActions } from "../actions/programsActions";
import { statusActions } from "../actions/statusActions";
import { getSubFormData } from "../selectors";
import { storeError } from "./dataStorage/storeError";

const crmFetchDataUrl = `${siteLocationPrefix}/forms/data?${ProgramSearchParam}={0}`;
const saveCrmDataUrl = `${siteLocationPrefix}/forms/save?${FormIdSearchParam}={0}&${SubFormIdSearchParam}={1}`;
const submitCrmDataUrl = `${siteLocationPrefix}/forms/submit?${FormIdSearchParam}={0}`;
const deleteGameConceptCrmDataUrl = `${siteLocationPrefix}/forms/deleteGameConcept`;

const addGameConceptId = (url: string, gameConceptId: string) =>
  `${url}${url.includes("?") ? "&" : "?"}${GameConceptIdSearchParam}=${gameConceptId}`;

const addPitchConceptId = (url: string, pitchConceptId: string) =>
  `${url}${url.includes("?") ? "&" : "?"}${PitchConceptIdSearchParam}=${pitchConceptId}`;

export const getFetchCrmDataUrl = (program: Programs, conceptId?: string) => {
  const baseUrl = format(crmFetchDataUrl, program);
  if (program === Programs.Pitch) {
    return conceptId ? addPitchConceptId(baseUrl, conceptId) : baseUrl;
  }
  return conceptId ? addGameConceptId(baseUrl, conceptId) : baseUrl;
};

const formatUrlWithConceptId = (baseUrl: string, formId: string, conceptId?: string): string => {
  if (!conceptId) {
    return baseUrl;
  }

  if (formId === PitchInformationFormId) {
    return addPitchConceptId(baseUrl, conceptId);
  }

  return addGameConceptId(baseUrl, conceptId);
};

export const getSaveCrmDataUrl = (formId: string, subFormId: string, conceptId?: string) => {
  const baseUrl = format(saveCrmDataUrl, formId, subFormId);
  return formatUrlWithConceptId(baseUrl, formId, conceptId);
};

export const getSubmitCrmDataUrl = (formId: string, conceptId?: string) => {
  const baseUrl = format(submitCrmDataUrl, formId);
  return formatUrlWithConceptId(baseUrl, formId, conceptId);
};

export const getDeleteGameConceptCrmDataUrl = (conceptId: string, formId: string) =>
  formatUrlWithConceptId(deleteGameConceptCrmDataUrl, formId, conceptId);

export function* fetchCrmDataStart(action: ReturnType<typeof crmActions.fetchCrmData.start>) {
  const isSigned: boolean | undefined = yield select(getIsUserSignedIn);

  if (!isSigned) {
    return;
  }

  try {
    const program = action.program;
    const gameConceptId = action.gameConceptId;

    if (gameConceptId) {
      yield put(statusActions.updateIsLoadingGameConcept(true));
    }

    const response: Response = yield call(fetch, getFetchCrmDataUrl(program, gameConceptId));
    const responseData: ProgramsDataResponse | undefined = yield call([response, "json"]);
    const data = responseData ? ({ ...responseData, program } as ProgramsDataResponse) : null;
    if (data) {
      yield put(programsActions.addProgramData(program, { ...data, capabilities: undefined }));
      if (data.capabilities) {
        yield put(capabilitiesActions.updateCapabilities.success(data.capabilities));
      }
      // Mark initial CRM fetch as completed to avoid making extra calls. This is used for page navigation today only.
      // Lookup useFetchCrmData in dataFetchingHelpers.ts
      yield put(statusActions.initialCrmFetchCompleted(action.pageType));
    }
  } catch (e) {
    yield storeError(e, OnboardingErrorType.InitialCrmFetch);
  }
  yield put(statusActions.updateIsLoadingGameConcept(false));
}

export function* saveCrmDataStart(action: ReturnType<typeof crmActions.saveCrmData.start>) {
  const isSigned: boolean | undefined = yield select(getIsUserSignedIn);
  if (!isSigned) {
    return;
  }

  yield storeError(undefined, OnboardingErrorType.Save);
  yield put(statusActions.updateIsSavingForm(true));

  const program = action.program;
  const formId = action.formId;
  const subFormId = action.subFormId;
  // For new game concept applications, we must ensure an empty string is sent into the request.
  // By default, we store DefaultNewGameConceptId as our game concept Id for new application.
  // We should straight get a new Id once the first save occurs.
  const conceptId = action.conceptId === DefaultNewGameConceptId ? undefined : action.conceptId;
  const subFormData: SubFormsDataTypes | undefined = yield select(
    getSubFormData,
    formId,
    subFormId
  );

  const formsRequestBody = {
    [formId]: {
      [subFormId]: subFormData,
    },
  };
  try {
    const response: Response = yield call(fetch, getSaveCrmDataUrl(formId, subFormId, conceptId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formsRequestBody),
    });
    const data:
      | ProgramsDataResponse
      | AaaGameConceptResponse
      | IdXboxGameConceptResponse
      | PitchConceptResponse = yield call([response, "json"]);
    if (data) {
      const aaaGameConceptResponse = (data as AaaGameConceptResponse)?.[AaaGameInformationFormId];
      const gameConceptResponse = (data as IdXboxGameConceptResponse)?.[GameInformationFormId];
      const pitchConceptResponse = (data as PitchConceptResponse)?.[PitchInformationFormId];
      if (gameConceptResponse || pitchConceptResponse || aaaGameConceptResponse) {
        switch (program) {
          case Programs.Aaa:
            yield put(gameConceptActions.updateSingleAaaGameConcept(aaaGameConceptResponse));
            break;
          case Programs.IdXbox:
            yield put(gameConceptActions.updateSingleIdXboxGameConcept(gameConceptResponse));
            break;
          case Programs.Pitch:
            yield put(pitchConceptActions.updateSinglePitchConcept(pitchConceptResponse));
            break;
        }
      } else {
        yield put(programsActions.addProgramData(program, data as ProgramsDataResponse));
      }
    }
  } catch (e) {
    yield storeError(e, OnboardingErrorType.Save);
  }

  yield put(statusActions.updateIsSavingForm(false));
}

export function* submitCrmDataStart(action: ReturnType<typeof crmActions.submitCrmData.start>) {
  const isSigned: boolean | undefined = yield select(getIsUserSignedIn);
  if (!isSigned) {
    return;
  }

  yield storeError(undefined, OnboardingErrorType.Submit);
  yield put(statusActions.updateIsSubmitting(true));

  const formId = action.formId;
  const conceptId = action.conceptId;

  try {
    const response: Response = yield call(fetch, getSubmitCrmDataUrl(formId, conceptId), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data: ProgramsDataResponse = yield call([response, "json"]);
    if (data) {
      yield put(programsActions.addProgramData(action.program, data));

      if (data.capabilities) {
        yield put(capabilitiesActions.updateCapabilities.success(data.capabilities));
      }
    }
  } catch (e) {
    yield storeError(e, OnboardingErrorType.Submit);
  }

  yield put(statusActions.updateIsSubmitting(false));
}

export function* deleteCrmGameConceptStart(
  action: ReturnType<typeof crmActions.deleteCrmGameConcept.start>
) {
  const conceptId = action.gameConceptId as string;
  if (!conceptId) {
    return;
  }

  const isSigned: boolean | undefined = yield select(getIsUserSignedIn);
  if (!isSigned) {
    return;
  }

  yield storeError(undefined, OnboardingErrorType.DeleteGameConcept);
  const program = action.program;
  try {
    const response: Response = yield call(
      fetch,
      getDeleteGameConceptCrmDataUrl(
        conceptId,
        program === Programs.Pitch ? PitchInformationFormId : ""
      )
    );
    if (response.status === 200) {
      const searchParams = new URLSearchParams(window.location.search);
      switch (program) {
        case Programs.IdXbox:
          yield put(
            crmActions.fetchCrmData.start(
              PageType.IdXboxApplication,
              Programs.IdXbox,
              searchParams.get(GameConceptIdSearchParam) ?? undefined
            )
          );
          break;
        case Programs.Pitch:
          yield put(
            crmActions.fetchCrmData.start(PageType.PitchApplication, Programs.Pitch, undefined)
          );
          break;
        case Programs.Aaa:
          yield put(
            crmActions.fetchCrmData.start(
              PageType.AaaApplication,
              Programs.Aaa,
              searchParams.get(GameConceptIdSearchParam) ?? undefined
            )
          );
          break;
      }
    }
  } catch (e) {
    yield storeError(e, OnboardingErrorType.DeleteGameConcept);
  }
}

export function* crmSagas() {
  yield takeEvery(CrmActions.FETCH_CRM_DATA_START, fetchCrmDataStart);
  yield takeEvery(CrmActions.SAVE_CRM_DATA_START, saveCrmDataStart);
  yield takeEvery(CrmActions.SUBMIT_CRM_DATA_START, submitCrmDataStart);
  yield takeEvery(CrmActions.DELETE_GAME_CONCEPT_START, deleteCrmGameConceptStart);
}
